import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import Foldout from 'components/shared/Foldout';
import { FormContainer as Form } from 'components/shared/form/Form';
import FormHeader from 'components/shared/form/FormHeader';
import FormSection from 'components/shared/form/FormSection';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import { DELIMITER_OPTS, IMPORTED_PATIENT, PATIENT_MAPPINGS } from 'constants/importConfigurations';
import { useProfile } from 'context/profile';
import { useAdminForm } from 'hooks/datasource';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { Classification } from 'models/flexible/Classification';
import { Client } from 'models/flexible/Client';
import { Group } from 'models/flexible/Group';
import { GroupType, GroupTypeApiName } from 'models/flexible/GroupType';
import { ImportConfiguration, ImportConfigurationOptions } from 'models/flexible/ImportConfiguration';
import { ClassificationType, GroupTypeType } from 'models/flexible/ImportConfiguration';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminIndexClassifications } from 'services/api/admin/classifications';
import { adminIndexClients } from 'services/api/admin/clients';
import { adminIndexGroups } from 'services/api/admin/groups';
import { indexGroupTypes } from 'services/api/admin/groupTypes';
import {
  adminCreateImportConfiguration,
  adminImportConfigurationsQueryKeys,
  adminShowImportConfiguration,
  adminUpdateImportConfiguration,
} from 'services/api/admin/importConfigurations';
import type { GroupTypeFields } from 'services/api/fields';
import { colors } from 'styles/theme/colors';
import DetailsIcon from 'svg/DetailsIcon';
import FormIndent from 'svg/FormIndent';
import RightArrowIcon from 'svg/RightArrowIcon';
import TrashIcon from 'svg/TrashIcon';

import LookupsSlideout, { LookupName } from './LookupsSlideout';

// ---------------------------------------------------------------------------
// Constants + helpers
// ---------------------------------------------------------------------------

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;
const getApiName = (o: any) => o!.apiName as string;
const getDisplayName = (o: any) => o!.displayName as string;

const CLASSIFICATION_TYPE_OPTIONS = [
  new ClassificationType({ name: 'Episode Type', apiName: 'episode' }),
  new ClassificationType({ name: 'Plan Type', apiName: 'plan_type' }),
];

const GROUP_TYPE_TYPE_OPTIONS = [
  new GroupTypeType({ displayName: 'Care Option', apiName: 'provider' }),
  new GroupTypeType({ displayName: 'Manager', apiName: 'manager' }),
];

const defaultConfig = {
  dateFormat: '%m/%d/%Y',
  delimiter: DELIMITER_OPTS[0],
  properties: {},
  lookups: { episodes: {}, plan_types: {}, groups: {}, group_types: {} },
  mappings: {
    classifications: [] as any[],
    groups: [] as any[],
    groupTypes: [] as any[],
    transferredFrom: { columnName: '', type: null, defaultValue: null },
  },
};

const validationSchema = yup.object({
  client: yup
    .object({ id: yup.string(), name: yup.string() })
    .nullable()
    .test('client', 'Client is required', (obj) => Boolean(obj && obj.id)),
  config: yup.object({
    dateFormat: yup.string().required('Date Format is required'),
    delimiter: yup
      .object({ label: yup.string(), value: yup.string() })
      .nullable()
      .test('delimiter', 'Delimiter is required', (obj) => Boolean(obj && obj.value)),
    mappings: yup.object({
      dateOfBirth: yup.string().required('Date of Birth is required'),
      externalId: yup.string().required('Patient ID is required'),
      name: yup.string().required('Patient Name is required'),
      sex: yup.string().required('Gender is required'),
      classifications: yup
        .array()
        .of(
          yup
            .object({
              columnName: yup.string().nullable(),
              defaultValue: yup.object({ id: yup.string(), name: yup.string() }).nullable(),
              type: yup
                .object({ apiName: yup.string(), name: yup.string() })
                .required('Classification Type is required'),
            })
            .test(
              'columnNameOrDefaultValue',
              'Either Client Header or Default Value is required',
              (value) => !!(value.columnName || value.defaultValue)
            )
        )
        .test('onlyOneMappingPerClassificationType', 'Only one mapping per Classification Type can exist', (value) => {
          if (!value) return true;
          const apiNames = value.map((item) => item.type?.apiName);
          return apiNames.length === new Set(apiNames).size;
        }),
      groups: yup
        .array()
        .of(
          yup
            .object({
              columnName: yup.string().nullable(),
              defaultValue: yup.object({ id: yup.string(), name: yup.string() }).nullable(),
              type: yup.object({ apiName: yup.string(), name: yup.string() }).required('Group Type is required'),
            })
            .test(
              'columnNameOrDefaultValue',
              'Either Client Header or Default Value is required',
              (value) => !!(value.columnName || value.defaultValue)
            )
        )
        .test('onlyOneMappingPerGroupType', 'Only one mapping per Group Type can exist', (value) => {
          if (!value) return true;
          const apiNames = value.map((item) => item.type?.apiName);
          return apiNames.length === new Set(apiNames).size;
        }),
      groupTypes: yup
        .array()
        .of(
          yup
            .object({
              columnName: yup.string().nullable(),
              type: yup.object({ apiName: yup.string(), name: yup.string() }).required('Category is required'),
            })
            .test('columnNameOrCategory', 'Client Header is required', (value) => !!value.columnName)
        )
        .test('onlyOneMappingPerCategory', 'Only one mapping per Category can exist', (value) => {
          if (!value) return true;
          const apiNames = value.map((item) => item.type?.apiName);
          return apiNames.length === new Set(apiNames).size;
        }),
    }),
  }),
});

// ---------------------------------------------------------------------------
// Mapping sub-sections
// ---------------------------------------------------------------------------

function ClassificationMappings({
  values,
  setValue,
  disabled,
  onOpenSlideout,
}: {
  values: any;
  setValue: (key: string, value: any) => void;
  disabled: boolean;
  onOpenSlideout: (name: LookupName) => void;
}) {
  const classifications = values.config.mappings.classifications ?? [];
  const [focusedType, setFocusedType] = useState<string | null>(classifications[0]?.type?.apiName ?? null);

  const availableTypeOptions = CLASSIFICATION_TYPE_OPTIONS.filter(
    (opt) => !classifications.some((c: any) => c.type?.apiName === opt.apiName)
  );

  const classificationOptions = useInfiniteOptions({
    queryKey: ['adminClassifications', 'importConfig', focusedType, values.client?.id],
    fetchFn: adminIndexClassifications,
    params: { client: values.client?.id, type: focusedType ?? '', sortBy: 'name asc' },
  });

  const addMapping = () =>
    setValue('config.mappings.classifications', [
      ...classifications,
      { defaultValue: null, type: null, columnName: '' },
    ]);
  const removeMapping = (i: number) =>
    setValue(
      'config.mappings.classifications',
      classifications.filter((_: any, idx: number) => idx !== i)
    );

  return (
    <>
      <SectionHeader>Classification Mappings</SectionHeader>
      {classifications.map((classification: any, index: number) => {
        const { type } = classification;
        const selectedType = CLASSIFICATION_TYPE_OPTIONS.find((o) => o.apiName === type?.apiName) ?? null;
        return (
          <MappingRowWithSubRow key={`${type?.apiName}-${index}`}>
            <MappingRow>
              <StyledInputGroup title='Client Header (optional)'>
                <Input
                  value={classification.columnName ?? ''}
                  onChange={(e) => setValue(`config.mappings.classifications[${index}].columnName`, e.target.value)}
                  disabled={disabled || !type}
                />
              </StyledInputGroup>
              <IconContainer>
                <RightArrowIcon />
              </IconContainer>
              <StyledInputGroup title='Classification Type'>
                <Select<ClassificationType>
                  options={availableTypeOptions}
                  value={selectedType}
                  onChange={(v) => setValue(`config.mappings.classifications[${index}].type`, v)}
                  getOptionValue={getApiName}
                  getOptionLabel={getName}
                  placeholder='Classification'
                  disabled={disabled}
                />
              </StyledInputGroup>
              {selectedType && (
                <IconContainer width='36px'>
                  <StyledDetailsIcon onClick={() => onOpenSlideout(`${selectedType.apiName}s` as LookupName)} />
                </IconContainer>
              )}
              <IconContainer width='36px'>
                {!disabled && <StyledTrashIcon onClick={() => removeMapping(index)} />}
              </IconContainer>
            </MappingRow>
            <MappingRow>
              <StyledFormIndent />
              <InputGroup title='Default Value (optional)'>
                <Select<Classification>
                  {...classificationOptions}
                  value={classification.defaultValue ?? null}
                  onChange={(v) => setValue(`config.mappings.classifications[${index}].defaultValue`, v)}
                  getOptionValue={getId}
                  getOptionLabel={getName}
                  onFocus={() => setFocusedType(classifications[index].type?.apiName ?? null)}
                  disabled={disabled || !type}
                  isClearable
                />
              </InputGroup>
            </MappingRow>
          </MappingRowWithSubRow>
        );
      })}
      {!disabled && availableTypeOptions.length > 0 && (
        <Link onClick={addMapping}>Add {classifications.length > 0 ? 'another' : 'a'} classification mapping</Link>
      )}
    </>
  );
}

function GroupTypeMappings({
  values,
  setValue,
  disabled,
  onOpenSlideout,
}: {
  values: any;
  setValue: (key: string, value: any) => void;
  disabled: boolean;
  onOpenSlideout: (name: LookupName) => void;
}) {
  const groupTypes = values.config.mappings.groupTypes ?? [];
  const availableOptions = GROUP_TYPE_TYPE_OPTIONS.filter(
    (opt) => !groupTypes.some((gt: any) => gt.type?.apiName.endsWith(opt.apiName))
  );

  const addMapping = () =>
    setValue('config.mappings.groupTypes', [...groupTypes, { defaultValue: null, type: null, columnName: '' }]);
  const removeMapping = (i: number) =>
    setValue(
      'config.mappings.groupTypes',
      groupTypes.filter((_: any, idx: number) => idx !== i)
    );

  return (
    <>
      <SectionHeader>Group Type Mappings</SectionHeader>
      {groupTypes.map((group: any, index: number) => {
        const { type } = group;
        const selectedType = GROUP_TYPE_TYPE_OPTIONS.find((o) => o.apiName === type?.apiName) ?? null;
        return (
          <MappingRow key={`${type?.apiName}-${index}`}>
            <StyledInputGroup title='Client Header'>
              <Input
                value={group.columnName ?? ''}
                onChange={(e) => setValue(`config.mappings.groupTypes[${index}].columnName`, e.target.value)}
                disabled={disabled || !type}
              />
            </StyledInputGroup>
            <IconContainer>
              <RightArrowIcon />
            </IconContainer>
            <StyledInputGroup title='Category'>
              <Select<GroupTypeType>
                options={availableOptions}
                value={selectedType}
                onChange={(v) => setValue(`config.mappings.groupTypes[${index}].type`, v)}
                getOptionValue={getApiName}
                getOptionLabel={getDisplayName}
                placeholder='Category'
                disabled={disabled}
              />
            </StyledInputGroup>
            <IconContainer width='36px'>
              <StyledDetailsIcon onClick={() => onOpenSlideout(LookupName.GroupTypes)} />
            </IconContainer>
            <IconContainer width='36px'>
              {!disabled && <StyledTrashIcon onClick={() => removeMapping(index)} />}
            </IconContainer>
          </MappingRow>
        );
      })}
      {!disabled && availableOptions.length > 0 && (
        <Link onClick={addMapping}>Add {groupTypes.length > 0 ? 'another' : 'a'} group type mapping</Link>
      )}
    </>
  );
}

function GroupMappings({
  values,
  setValue,
  disabled,
  onOpenSlideout,
}: {
  values: any;
  setValue: (key: string, value: any) => void;
  disabled: boolean;
  onOpenSlideout: (name: LookupName) => void;
}) {
  const config = values.config;
  const groups = config.mappings.groups ?? [];
  const [focusedGroupType, setFocusedGroupType] = useState<GroupType | null>(groups[0]?.type ?? null);

  const mappingGroupTypeOptions = config.mappings.groupTypes.map((mapping: any) => {
    const match = GROUP_TYPE_TYPE_OPTIONS.find((o) => mapping.type?.apiName.endsWith(o.apiName));
    return new GroupType({
      id: '',
      apiName: `mappings:group_types:${mapping.type?.apiName}`,
      displayName: '',
      name: `Mapping: ${mapping.columnName}`,
      isInpatient: false,
      config: {},
      createdAt: null,
      updatedAt: null,
      type: match
        ? { apiName: match.apiName as GroupTypeApiName, displayName: match.displayName }
        : { apiName: GroupTypeApiName.PROVIDER, displayName: '' },
    });
  });

  const groupTypeSelectOptions = useInfiniteOptions({
    queryKey: ['adminGroupTypes', 'groupMappings'],
    fetchFn: indexGroupTypes,
    params: { 'apiName.not': 'physician_team', sortBy: 'displayName asc' },
  });

  const groupSelectOptions = useInfiniteOptions({
    queryKey: ['adminGroups', 'groupMappings', focusedGroupType?.apiName, values.client?.id],
    fetchFn: adminIndexGroups,
    params: { clientId: values.client?.id, type: focusedGroupType?.apiName ?? '', include: 'groupType' },
  });

  const getGroupTypeLabel = (gt: GroupType<GroupTypeFields>) => `${gt.name} (${gt.type?.displayName ?? ''})`;

  const addMapping = () =>
    setValue('config.mappings.groups', [...groups, { defaultValue: null, type: null, columnName: '' }]);
  const removeMapping = (i: number) =>
    setValue(
      'config.mappings.groups',
      groups.filter((_: any, idx: number) => idx !== i)
    );

  return (
    <>
      <SectionHeader>Group Mappings</SectionHeader>
      {groups.map((group: any, index: number) => {
        const { type } = group;
        return (
          <MappingRowWithSubRow key={`${type?.apiName}-${index}`}>
            <MappingRow>
              <StyledInputGroup title='Client Header (optional)'>
                <Input
                  value={group.columnName ?? ''}
                  onChange={(e) => setValue(`config.mappings.groups[${index}].columnName`, e.target.value)}
                  disabled={disabled || !type}
                />
              </StyledInputGroup>
              <IconContainer>
                <RightArrowIcon />
              </IconContainer>
              <StyledInputGroup title='Group Type'>
                <Select<GroupType>
                  {...groupTypeSelectOptions}
                  prependedOptions={mappingGroupTypeOptions}
                  value={type}
                  onChange={(v) => setValue(`config.mappings.groups[${index}].type`, v)}
                  getOptionValue={getApiName}
                  getOptionLabel={getGroupTypeLabel}
                  placeholder='Group Type'
                  disabled={disabled}
                />
              </StyledInputGroup>
              <IconContainer width='36px'>
                <StyledDetailsIcon onClick={() => onOpenSlideout(LookupName.Groups)} />
              </IconContainer>
              <IconContainer width='36px'>
                {!disabled && <StyledTrashIcon onClick={() => removeMapping(index)} />}
              </IconContainer>
            </MappingRow>
            <MappingRow>
              <StyledFormIndent />
              <InputGroup title='Default Value (optional)'>
                <Select<Group>
                  {...groupSelectOptions}
                  value={group.defaultValue ?? null}
                  onChange={(v) => setValue(`config.mappings.groups[${index}].defaultValue`, v)}
                  getOptionValue={getId}
                  getOptionLabel={getName}
                  onFocus={() => setFocusedGroupType(groups[index].type)}
                  disabled={disabled || !type}
                  isClearable
                />
              </InputGroup>
            </MappingRow>
          </MappingRowWithSubRow>
        );
      })}
      {!disabled && <Link onClick={addMapping}>Add {groups.length > 0 ? 'another' : 'a'} group mapping</Link>}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function EditImportConfiguration() {
  const { id } = useParams<{ id: string }>();
  const { profile } = useProfile();
  const isEditing = !!id;
  const canEdit = profile.has(PERMISSIONS.adminImportConfigurationEdit);
  const disabled = !canEdit;

  const { values, errors, valid, dirty, setValue, submit, loading, goBack } = useAdminForm({
    id,
    show: adminShowImportConfiguration,
    create: adminCreateImportConfiguration,
    update: adminUpdateImportConfiguration,
    include: ['client'] as const,
    queryKey: adminImportConfigurationsQueryKeys,
    schema: validationSchema,
    onCreateSuccess: {
      toast: 'Import Configuration successfully added!',
      navigate: '/admin/clients/import-configurations',
    },
    onUpdateSuccess: {
      toast: 'Import Configuration successfully updated!',
      navigate: '/admin/clients/import-configurations',
    },
    onError: { toast: 'Something went wrong.' },
    toFormValues: (data) => ({
      id: data?.id ?? '',
      client: data?.client ?? (null as { id: string; name: string } | null),
      config: {
        ...defaultConfig,
        ...data?.config,
        dateFormat: data?.config?.dateFormat || '%m/%d/%Y',
        delimiter:
          DELIMITER_OPTS.find(
            (opt) =>
              opt.value ===
              (typeof data?.config?.delimiter === 'object' ? data?.config?.delimiter?.value : data?.config?.delimiter)
          ) ?? DELIMITER_OPTS[0],
        lookups: { ...defaultConfig.lookups, ...data?.config?.lookups },
        mappings: {
          ...defaultConfig.mappings,
          ...data?.config?.mappings,
          groupTypes: data?.config?.mappings?.groupTypes || [],
        },
      },
    }),
    toPayload: (v) => {
      const ic = new ImportConfiguration({
        id: v.id,
        clientId: v.client?.id ?? '',
        type: IMPORTED_PATIENT,
        updatedAt: '',
        config: {
          ...v.config,
          delimiter: typeof v.config.delimiter === 'object' ? (v.config.delimiter?.value ?? ',') : v.config.delimiter,
        },
      } as ImportConfigurationOptions);
      return ic.serialize();
    },
  });

  const config = values.config;

  // Dropdown options
  const clientOptions = useInfiniteOptions({
    queryKey: ['adminClients', 'importConfig'],
    fetchFn: adminIndexClients,
    params: { sortBy: 'name asc', hasImportConfiguration: false },
  });

  const [hospitalGroupType, setHospitalGroupType] = useState<GroupType | null>(null);
  useEffect(() => {
    indexGroupTypes({ apiName: 'hospital' }).then((res) => {
      if (res.data.length > 0) setHospitalGroupType(res.data[0]);
    });
  }, []);

  // Slideout state
  const [selectedLookupName, setSelectedLookupName] = useState<LookupName | null>(null);
  const [showSlideout, setShowSlideout] = useState(false);

  const handleOpenSlideout = (lookupName: LookupName) => {
    setSelectedLookupName(lookupName);
    setShowSlideout(true);
  };

  if (isEditing && loading) {
    return (
      <AdminFormPage>
        <CircularProgress />
      </AdminFormPage>
    );
  }

  return (
    <AdminFormPage>
      <Form>
        <FormHeader title={isEditing ? 'Edit Import Configuration' : 'Add Import Configuration'} />

        <FormSection>
          <InputGroup title='Client' error={errors.client as string}>
            <Select<Client>
              {...clientOptions}
              name='client'
              value={(values.client as Client) ?? null}
              onChange={(v) => setValue('client', v)}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Client'
              disabled={disabled || isEditing}
              hasError={!!errors.client}
            />
          </InputGroup>
        </FormSection>

        <FormSection>
          <SectionHeader>File Format</SectionHeader>
          <InputGroup title='Delimiter' error={(errors.config as any)?.delimiter}>
            <Select
              options={DELIMITER_OPTS}
              name='delimiter'
              value={config.delimiter}
              onChange={(v) => setValue('config.delimiter', v)}
              getOptionValue={(o) => o.value}
              getOptionLabel={(o) => o.label}
              placeholder='Delimiter'
              disabled={disabled}
              hasError={!!(errors.config as any)?.delimiter}
            />
          </InputGroup>
          <InputGroup title='Date Format' error={(errors.config as any)?.dateFormat}>
            <Input
              value={config.dateFormat ?? ''}
              onChange={(e) => setValue('config.dateFormat', e.target.value)}
              placeholder='Date Format'
              disabled={disabled}
              $hasError={!!(errors.config as any)?.dateFormat}
            />
          </InputGroup>
        </FormSection>

        <FormSection>
          <SectionHeader>Patient Demographic Mappings</SectionHeader>
          {PATIENT_MAPPINGS.map(({ key, label, optional }) => (
            <MappingRow key={key}>
              <StyledInputGroup title={`Client Header ${optional ? '(optional)' : ''}`}>
                <Input
                  value={(config.mappings as any)[key] ?? ''}
                  onChange={(e) => setValue(`config.mappings.${key}`, e.target.value)}
                  disabled={disabled}
                />
              </StyledInputGroup>
              <IconContainer>
                <RightArrowIcon />
              </IconContainer>
              <StyledInputGroup title='Olio Field'>
                <Input value={label} readOnly />
              </StyledInputGroup>
              <Spacer />
            </MappingRow>
          ))}
          <MappingRow>
            <StyledInputGroup title='Client Header (optional)'>
              <Input
                value={config.mappings.transferredFrom?.columnName ?? ''}
                onChange={(e) => setValue('config.mappings.transferredFrom.columnName', e.target.value)}
                disabled={disabled}
              />
            </StyledInputGroup>
            <IconContainer>
              <RightArrowIcon />
            </IconContainer>
            <StyledInputGroup title='Transferred From'>
              <Select<GroupType>
                options={hospitalGroupType ? [hospitalGroupType] : []}
                value={hospitalGroupType}
                onChange={() => {}}
                getOptionValue={getId}
                getOptionLabel={getName}
                disabled
              />
            </StyledInputGroup>
            <IconContainer width='36px'>
              <DetailsIcon
                style={{ color: 'rgba(15,18,38,.5)', cursor: 'pointer', width: 16 }}
                onClick={() => handleOpenSlideout(LookupName.Groups)}
              />
            </IconContainer>
          </MappingRow>

          <ClassificationMappings
            values={values}
            setValue={setValue}
            disabled={disabled}
            onOpenSlideout={handleOpenSlideout}
          />
          <GroupTypeMappings
            values={values}
            setValue={setValue}
            disabled={disabled}
            onOpenSlideout={handleOpenSlideout}
          />
          <GroupMappings values={values} setValue={setValue} disabled={disabled} onOpenSlideout={handleOpenSlideout} />
        </FormSection>

        <ButtonGroup>
          <Button variant='ghost' onClick={goBack}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={disabled || !dirty || !valid}>
            Submit
          </Button>
        </ButtonGroup>
      </Form>

      <Foldout
        open={showSlideout}
        position='right'
        width='min(80%, 917px)'
        onBackdropClick={() => setShowSlideout(false)}>
        {selectedLookupName && (
          <LookupsSlideout
            lookupName={selectedLookupName}
            lookups={config.lookups[selectedLookupName] || {}}
            handleClose={() => setShowSlideout(false)}
            handleApply={(_lookupName, lookups) => {
              if (selectedLookupName) setValue(`config.lookups.${selectedLookupName}`, lookups);
              setShowSlideout(false);
            }}
            disabled={disabled}
          />
        )}
      </Foldout>
    </AdminFormPage>
  );
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const IconContainer = styled.div<{ width?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${(props) => props.width || '60px'};
`;

const Link = styled.a`
  color: ${colors.primaryBlue};
  cursor: pointer;
  display: flex;
  margin-bottom: 24px;
  font-size: 14px;
`;

const MappingRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const MappingRowWithSubRow = styled(MappingRow)`
  flex-direction: column;
  & > :last-child {
    align-self: flex-end;
    margin-top: -16px;
    width: 40%;
  }
`;

const StyledInputGroup = styled(InputGroup)`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StyledFormIndent = styled(FormIndent)`
  margin-right: 8px;
`;

const StyledDetailsIcon = styled(DetailsIcon)`
  color: ${colors.black50};
  cursor: pointer;
  width: 16px;
`;

const StyledTrashIcon = styled(TrashIcon)`
  color: ${colors.accentRed};
  cursor: pointer;
`;

const Spacer = styled.div`
  width: 36px;
  flex-shrink: 0;
`;
